import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Slider,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import {
  Editor,
  EditorProvider,
  Toolbar,
  BtnUndo,
  BtnRedo,
  Separator,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnNumberedList,
  BtnBulletList,
  BtnLink,
  BtnClearFormatting,
  BtnStyles,
  HtmlButton,
} from 'react-simple-wysiwyg';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;
const MASKED_SECRET = '*******************************';
const KEEP_SECRET_SENTINEL = '***KEEP_EXISTING***';
const MAX_IMAGE_URL_LENGTH = 2048;
const MAX_IMAGE_WIDTH = 600;
const MIN_IMAGE_WIDTH = 80;
const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.avif'];
const IMAGE_PRESETS = {
  small: 180,
  medium: 320,
  large: 520,
};
const IMAGE_ALIGNMENT_VALUES = ['left', 'center', 'right'];

const defaultCredentials = () => ({
  imapHost: '',
  imapPort: '143',
  imapUseSsl: true,
  imapUsername: '',
  imapPassword: '',
  imapFolder: 'INBOX',
  smtpHost: '',
  smtpPort: '25',
  smtpUseTls: true,
  smtpUsername: '',
  smtpPassword: '',
  fromEmail: '',
  fromDisplayName: '',
  defaultReplyCcEmails: '',
  defaultReplyBccEmails: '',
  replyHeaderHtml: '',
  replyFooterHtml: '',
  enabled: true,
});

const rowSx = { display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' };
const halfFieldSx = { flex: 1, minWidth: 220 };
const denseFieldProps = { margin: 'dense', size: 'small', InputLabelProps: { shrink: true } };
const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  '&:before': { display: 'none' },
  '&:not(:last-child)': { borderBottom: 0 },
};
const accordionSummarySx = { minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } };
const wysiwygBoxSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 160,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  '& .rsw-toolbar, & .r-s-wysiwyg-toolbar': {
    marginBottom: 0,
    flex: '0 0 auto',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .rsw-editor, & .rsw-content, & [contenteditable="true"]': {
    flex: '1 1 auto',
    minHeight: 100,
    maxHeight: 220,
    overflow: 'auto',
    padding: '8px 12px',
    boxSizing: 'border-box',
    margin: 0,
  },
  '& textarea': {
    flex: '1 1 auto',
    minHeight: 100,
    maxHeight: 220,
    width: '100%',
    overflow: 'auto',
    fontFamily: 'monospace',
    padding: '8px 12px',
    boxSizing: 'border-box',
    margin: 0,
    lineHeight: 1.4,
  },
};

const EmailCredentialsModal = ({
  open,
  onClose,
  onCredentialsSaved,
  tenantId,
  agentId,
  productName = 'Chattitude',
}) => {
  const headerEditorContainerRef = useRef(null);
  const footerEditorContainerRef = useRef(null);
  const selectedImageRef = useRef(null);
  const savedSelectionRef = useRef({ section: null, range: null });

  const [credentials, setCredentials] = useState(defaultCredentials());
  const [hasExistingSecrets, setHasExistingSecrets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [sendTestEmail, setSendTestEmail] = useState(false);
  const [expandedSection, setExpandedSection] = useState('connection');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [imageDialog, setImageDialog] = useState({
    open: false,
    section: 'header',
    mode: 'insert',
    imageUrl: '',
    altText: '',
    width: IMAGE_PRESETS.medium,
    preset: 'medium',
    alignment: 'left',
    validationError: '',
  });

  const toggleSection = (section) => {
    setExpandedSection((current) => (current === section ? false : section));
  };

  const openAppearance = () => setExpandedSection('appearance');

  const getEditorContainer = (section) => (section === 'header'
    ? headerEditorContainerRef.current
    : footerEditorContainerRef.current);

  const getEditorContentElement = (section) => {
    const container = getEditorContainer(section);
    return container?.querySelector('[contenteditable="true"]') || null;
  };

  const getImageUrlValidationError = (rawUrl) => {
    const imageUrl = (rawUrl || '').trim();
    if (!imageUrl) return 'Image URL is required.';
    if (imageUrl.length > MAX_IMAGE_URL_LENGTH) {
      return `Image URL is too long (max ${MAX_IMAGE_URL_LENGTH} characters).`;
    }

    // Basic control character check before URL parsing.
    if (/\s|[\u0000-\u001F\u007F]/.test(imageUrl)) {
      return 'Image URL cannot contain spaces or control characters.';
    }

    let parsed;
    try {
      parsed = new URL(imageUrl);
    } catch (_err) {
      return 'Use a valid URL.';
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Use a valid http:// or https:// URL.';
    }

    if (parsed.username || parsed.password) {
      return 'Image URL cannot include embedded username or password.';
    }

    const pathname = (parsed.pathname || '').toLowerCase();
    const hasAllowedExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    if (!hasAllowedExtension) {
      return `Image URL must end with one of: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}.`;
    }

    return '';
  };

  const isValidImageUrl = (url) => {
    return !getImageUrlValidationError(url);
  };

  const buildSafeImageHtml = ({ src, alt, width, alignment }) => {
    const img = document.createElement('img');
    img.setAttribute('src', src);
    img.setAttribute('alt', (alt || '').trim());
    img.setAttribute('width', String(width));
    img.setAttribute('data-align', alignment);
    img.setAttribute('style', getImageStyle(width, alignment));
    return img.outerHTML;
  };

  const clampImageWidth = (width) => {
    const num = Number(width);
    if (!Number.isFinite(num)) return IMAGE_PRESETS.medium;
    return Math.min(MAX_IMAGE_WIDTH, Math.max(MIN_IMAGE_WIDTH, Math.round(num)));
  };

  const getPresetFromWidth = (width) => {
    const presetEntry = Object.entries(IMAGE_PRESETS)
      .find(([_name, value]) => value === clampImageWidth(width));
    return presetEntry ? presetEntry[0] : 'custom';
  };

  const getAlignmentFromImage = (imgElement) => {
    const storedAlignment = imgElement.getAttribute('data-align');
    if (IMAGE_ALIGNMENT_VALUES.includes(storedAlignment)) {
      return storedAlignment;
    }
    const style = imgElement.getAttribute('style') || '';
    if (/margin-left:\s*auto/i.test(style) && /margin-right:\s*auto/i.test(style)) return 'center';
    if (/margin-left:\s*auto/i.test(style) && /margin-right:\s*0/i.test(style)) return 'right';
    return 'left';
  };

  const getWidthFromImage = (imgElement) => {
    const styleWidth = imgElement.style?.width ? parseInt(imgElement.style.width, 10) : NaN;
    if (Number.isFinite(styleWidth)) return clampImageWidth(styleWidth);
    const attrWidth = parseInt(imgElement.getAttribute('width'), 10);
    if (Number.isFinite(attrWidth)) return clampImageWidth(attrWidth);
    return IMAGE_PRESETS.medium;
  };

  const getImageStyle = (width, alignment) => {
    const clampedWidth = clampImageWidth(width);
    const safeAlignment = IMAGE_ALIGNMENT_VALUES.includes(alignment) ? alignment : 'left';
    const marginMap = {
      left: 'margin: 8px auto 8px 0;',
      center: 'margin: 8px auto;',
      right: 'margin: 8px 0 8px auto;',
    };

    return `width: ${clampedWidth}px; max-width: 100%; height: auto; display: block; ${marginMap[safeAlignment]}`;
  };

  const captureSelection = (section) => {
    const editorElement = getEditorContentElement(section);
    if (!editorElement) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount < 1) return;

    const range = selection.getRangeAt(0);
    if (!editorElement.contains(range.commonAncestorContainer)) return;

    savedSelectionRef.current = { section, range: range.cloneRange() };
  };

  const restoreSelection = (section) => {
    const savedSelection = savedSelectionRef.current;
    if (!savedSelection?.range || savedSelection.section !== section) return;

    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    selection.addRange(savedSelection.range);
  };

  const setSectionHtml = (section, html) => {
    if (section === 'header') {
      setCredentials((prev) => ({ ...prev, replyHeaderHtml: html }));
    } else {
      setCredentials((prev) => ({ ...prev, replyFooterHtml: html }));
    }
  };

  const syncSectionHtmlFromEditor = (section) => {
    const editorElement = getEditorContentElement(section);
    if (!editorElement) return;
    setSectionHtml(section, editorElement.innerHTML || '');
  };

  const clearImageDialog = () => {
    setImageDialog({
      open: false,
      section: 'header',
      mode: 'insert',
      imageUrl: '',
      altText: '',
      width: IMAGE_PRESETS.medium,
      preset: 'medium',
      alignment: 'left',
      validationError: '',
    });
    selectedImageRef.current = null;
  };

  const openImageDialogForInsert = (section) => {
    captureSelection(section);
    selectedImageRef.current = null;
    setImageDialog({
      open: true,
      section,
      mode: 'insert',
      imageUrl: '',
      altText: '',
      width: IMAGE_PRESETS.medium,
      preset: 'medium',
      alignment: 'left',
      validationError: '',
    });
  };

  const openImageDialogForEdit = (section, imageElement) => {
    selectedImageRef.current = imageElement;
    const width = getWidthFromImage(imageElement);
    setImageDialog({
      open: true,
      section,
      mode: 'edit',
      imageUrl: imageElement.getAttribute('src') || '',
      altText: imageElement.getAttribute('alt') || '',
      width,
      preset: getPresetFromWidth(width),
      alignment: getAlignmentFromImage(imageElement),
      validationError: '',
    });
  };

  const handleImageDialogFieldChange = (field, value) => {
    setImageDialog((current) => ({
      ...current,
      [field]: value,
      validationError: '',
    }));
  };

  const handleImagePresetChange = (preset) => {
    if (preset === 'custom') {
      setImageDialog((current) => ({ ...current, preset, validationError: '' }));
      return;
    }
    const presetWidth = IMAGE_PRESETS[preset] || IMAGE_PRESETS.medium;
    setImageDialog((current) => ({
      ...current,
      preset,
      width: presetWidth,
      validationError: '',
    }));
  };

  const handleImageWidthChange = (_evt, sliderWidth) => {
    const width = clampImageWidth(sliderWidth);
    setImageDialog((current) => ({
      ...current,
      width,
      preset: getPresetFromWidth(width),
      validationError: '',
    }));
  };

  const applyImageChanges = () => {
    const section = imageDialog.section;
    const imageUrl = imageDialog.imageUrl.trim();

    const urlValidationError = getImageUrlValidationError(imageUrl);
    if (urlValidationError) {
      setImageDialog((current) => ({ ...current, validationError: urlValidationError }));
      return;
    }

    const width = clampImageWidth(imageDialog.width);
    const alignment = IMAGE_ALIGNMENT_VALUES.includes(imageDialog.alignment)
      ? imageDialog.alignment
      : 'left';
    const style = getImageStyle(width, alignment);

    if (imageDialog.mode === 'edit' && selectedImageRef.current) {
      const selectedImage = selectedImageRef.current;
      selectedImage.setAttribute('src', imageUrl);
      selectedImage.setAttribute('alt', (imageDialog.altText || '').trim());
      selectedImage.setAttribute('width', String(width));
      selectedImage.setAttribute('data-align', alignment);
      selectedImage.setAttribute('style', style);
      syncSectionHtmlFromEditor(section);
      clearImageDialog();
      return;
    }

    const editorElement = getEditorContentElement(section);
    if (!editorElement) {
      setImageDialog((current) => ({
        ...current,
        validationError: 'Editor is not ready yet. Please try again.',
      }));
      return;
    }

    editorElement.focus();
    restoreSelection(section);

    const imgHtml = buildSafeImageHtml({
      src: imageUrl,
      alt: imageDialog.altText,
      width,
      alignment,
    });

    const inserted = document.execCommand('insertHTML', false, imgHtml);
    if (!inserted) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const wrapper = document.createElement('div');
        wrapper.innerHTML = imgHtml;
        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) {
          fragment.appendChild(wrapper.firstChild);
        }
        range.deleteContents();
        range.insertNode(fragment);
      } else {
        editorElement.innerHTML += imgHtml;
      }
    }

    syncSectionHtmlFromEditor(section);
    clearImageDialog();
  };

  const connectionSummary = credentials.fromEmail?.trim()
    || credentials.imapUsername?.trim()
    || credentials.imapHost?.trim()
    || 'Not configured';

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenantId || !agentId) {
        setCredentials(defaultCredentials());
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const jwtToken = Cookies.getJwtToken();
        const tenant = Cookies.getTenantId();
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

        const response = await axios.get(`${API_URL}/agent-email-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        const d = response.data;
        setCredentials({
          imapHost: d.imapHost || '',
          imapPort: String(d.imapPort ?? 993),
          imapUseSsl: d.imapUseSsl !== false,
          imapUsername: d.imapUsername || '',
          imapPassword: MASKED_SECRET,
          imapFolder: d.imapFolder || 'INBOX',
          smtpHost: d.smtpHost || '',
          smtpPort: String(d.smtpPort ?? 587),
          smtpUseTls: d.smtpUseTls !== false,
          smtpUsername: d.smtpUsername || '',
          smtpPassword: MASKED_SECRET,
          fromEmail: d.fromEmail || '',
          fromDisplayName: d.fromDisplayName || '',
          defaultReplyCcEmails: d.defaultReplyCcEmails || '',
          defaultReplyBccEmails: d.defaultReplyBccEmails || '',
          replyHeaderHtml: d.replyHeaderHtml || '',
          replyFooterHtml: d.replyFooterHtml || '',
          enabled: d.enabled !== false,
        });
        setHasExistingSecrets(true);
        setExpandedSection('connection');
      } catch (err) {
        if (err.response?.status === 404) {
          setCredentials(defaultCredentials());
          setHasExistingSecrets(false);
          setExpandedSection('connection');
        } else {
          setCredentials(defaultCredentials());
          setHasExistingSecrets(false);
          setError('Failed to fetch credentials.');
          setExpandedSection('connection');
        }
      } finally {
        setLoading(false);
      }
    };
    if (open && tenantId && agentId) {
      fetchCredentials();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open, tenantId, agentId]);

  useEffect(() => {
    if (!open || loading) {
      return undefined;
    }

    const bindImageClickHandler = (section) => {
      const editorElement = getEditorContentElement(section);
      if (!editorElement) {
        return () => { };
      }

      const onEditorClick = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLImageElement)) {
          return;
        }
        openImageDialogForEdit(section, target);
      };

      editorElement.addEventListener('click', onEditorClick);
      return () => {
        editorElement.removeEventListener('click', onEditorClick);
      };
    };

    const unbindHeader = bindImageClickHandler('header');
    const unbindFooter = bindImageClickHandler('footer');

    return () => {
      unbindHeader();
      unbindFooter();
    };
    // The helper functions used here rely on refs/state setters and do not need to trigger re-bind by identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, credentials.replyHeaderHtml, credentials.replyFooterHtml]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (hasExistingSecrets && (name === 'imapPassword' || name === 'smtpPassword') && value !== MASKED_SECRET) {
      setHasExistingSecrets(false);
    }
    setCredentials(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleReplyHeaderChange = (e) => {
    setCredentials(prev => ({ ...prev, replyHeaderHtml: e.target.value }));
  };

  const handleReplyFooterChange = (e) => {
    setCredentials(prev => ({ ...prev, replyFooterHtml: e.target.value }));
  };

  const renderImageToolbarButton = (section) => (
    <button
      type="button"
      className="rsw-btn"
      title="Insert image by URL"
      onMouseDown={(event) => {
        event.preventDefault();
        captureSelection(section);
      }}
      onClick={() => openImageDialogForInsert(section)}
      style={{ minWidth: 32 }}
    >
      <ImageIcon fontSize="small" />
    </button>
  );

  const renderTemplateEditor = ({ section, value, onChange, placeholder, containerRef }) => (
    <Box ref={containerRef} sx={wysiwygBoxSx}>
      <EditorProvider>
        <Editor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          containerProps={{
            style: {
              minHeight: 100,
              width: '100%',
            },
          }}
        >
          <Toolbar style={{ flexWrap: 'wrap', rowGap: 4 }}>
            <BtnUndo />
            <BtnRedo />
            <Separator />
            <BtnBold />
            <BtnItalic />
            <BtnUnderline />
            <BtnStrikeThrough />
            <Separator />
            <BtnNumberedList />
            <BtnBulletList />
            <Separator />
            <BtnLink />
            <BtnClearFormatting />
            <HtmlButton />
            <Separator />
            <BtnStyles />
            <Separator />
            {renderImageToolbarButton(section)}
          </Toolbar>
        </Editor>
      </EditorProvider>
    </Box>
  );

  const validate = () => {
    if (!credentials.imapHost?.trim()) return 'IMAP host is required.';
    if (!credentials.imapUsername?.trim()) return 'IMAP username is required.';
    if (!credentials.smtpHost?.trim()) return 'SMTP host is required.';
    if (!credentials.smtpUsername?.trim()) return 'SMTP username is required.';
    if (!credentials.fromEmail?.trim()) return 'From email is required.';
    if (!hasExistingSecrets) {
      if (!credentials.imapPassword?.trim()) return 'IMAP password is required.';
      if (!credentials.smtpPassword?.trim()) return 'SMTP password is required.';
    }
    return null;
  };

  const getAuthHeaders = () => {
    const jwtToken = Cookies.getJwtToken();
    const tenant = Cookies.getTenantId();
    return jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});
  };

  const buildRequestPayload = () => ({
    tenantId: parseInt(tenantId, 10),
    agentId: parseInt(agentId, 10),
    imapHost: credentials.imapHost.trim(),
    imapPort: parseInt(credentials.imapPort, 10),
    imapUseSsl: credentials.imapUseSsl,
    imapUsername: credentials.imapUsername.trim(),
    imapPassword: (hasExistingSecrets && credentials.imapPassword === MASKED_SECRET)
      ? KEEP_SECRET_SENTINEL : credentials.imapPassword,
    imapFolder: credentials.imapFolder.trim() || 'INBOX',
    smtpHost: credentials.smtpHost.trim(),
    smtpPort: parseInt(credentials.smtpPort, 10),
    smtpUseTls: credentials.smtpUseTls,
    smtpUsername: credentials.smtpUsername.trim(),
    smtpPassword: (hasExistingSecrets && credentials.smtpPassword === MASKED_SECRET)
      ? KEEP_SECRET_SENTINEL : credentials.smtpPassword,
    fromEmail: credentials.fromEmail.trim(),
    fromDisplayName: credentials.fromDisplayName.trim(),
    defaultReplyCcEmails: credentials.defaultReplyCcEmails.trim(),
    defaultReplyBccEmails: credentials.defaultReplyBccEmails.trim(),
    replyHeaderHtml: credentials.replyHeaderHtml,
    replyFooterHtml: credentials.replyFooterHtml,
    enabled: credentials.enabled,
  });

  const validateImapTest = () => {
    if (!credentials.imapHost?.trim()) return 'IMAP host is required to test.';
    if (!credentials.imapUsername?.trim()) return 'IMAP username is required to test.';
    if (!hasExistingSecrets && !credentials.imapPassword?.trim()) return 'IMAP password is required to test.';
    return null;
  };

  const validateSmtpTest = () => {
    if (!credentials.smtpHost?.trim()) return 'SMTP host is required to test.';
    if (!credentials.smtpUsername?.trim()) return 'SMTP username is required to test.';
    if (!hasExistingSecrets && !credentials.smtpPassword?.trim()) return 'SMTP password is required to test.';
    if (sendTestEmail && !credentials.fromEmail?.trim()) return 'From email is required to send a test message.';
    return null;
  };

  const handleTestImap = async () => {
    const validationError = validateImapTest();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setTestingImap(true);
    try {
      const response = await axios.post(
        `${API_URL}/agent-email-credentials/test-imap`,
        buildRequestPayload(),
        { headers: getAuthHeaders() }
      );
      setSnackbar({ open: true, message: response.data.message || 'IMAP connection successful.', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || 'IMAP connection test failed.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setTestingImap(false);
    }
  };

  const handleTestSmtp = async () => {
    const validationError = validateSmtpTest();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setTestingSmtp(true);
    try {
      const response = await axios.post(
        `${API_URL}/agent-email-credentials/test-smtp`,
        { ...buildRequestPayload(), sendTestEmail },
        { headers: getAuthHeaders() }
      );
      setSnackbar({ open: true, message: response.data.message || 'SMTP connection successful.', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || 'SMTP connection test failed.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/agent-email-credentials`, buildRequestPayload(), { headers: getAuthHeaders() });

      setSnackbar({ open: true, message: 'Email credentials saved.', severity: 'success' });
      if (onCredentialsSaved) onCredentialsSaved();
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save credentials.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const copyImapToSmtp = () => {
    setCredentials(prev => ({
      ...prev,
      smtpHost: prev.imapHost,
      smtpUsername: prev.imapUsername,
      smtpPassword: prev.imapPassword === MASKED_SECRET ? MASKED_SECRET : prev.imapPassword,
      fromEmail: prev.fromEmail || prev.imapUsername,
    }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 920,
            maxHeight: '78vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Email Support Inbox
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', py: 1.5 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {error && <Typography color="error" variant="body2">{error}</Typography>}

              <FormControlLabel
                control={<Switch name="enabled" checked={credentials.enabled} onChange={handleChange} size="small" />}
                label="Enable email polling"
                sx={{ ml: 0, my: 0.5 }}
              />

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Accordion
                  expanded={expandedSection === 'connection'}
                  onChange={() => toggleSection('connection')}
                  disableGutters
                  elevation={0}
                  sx={accordionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Mailbox connection</Typography>
                      {expandedSection !== 'connection' && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {connectionSummary}
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0, pb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>IMAP (inbound)</Typography>
                    <Box sx={rowSx}>
                      <TextField sx={{ flex: 2, minWidth: 260 }} {...denseFieldProps} label="IMAP Host" name="imapHost" value={credentials.imapHost} onChange={handleChange} />
                      <TextField sx={{ width: 110 }} {...denseFieldProps} label="Port - 143=STARTTLS" name="imapPort" value={credentials.imapPort} onChange={handleChange} />
                    </Box>
                    <FormControlLabel
                      control={<Switch name="imapUseSsl" checked={credentials.imapUseSsl} onChange={handleChange} size="small" />}
                      label="IMAP encryption - STARTTLS on 143, SSL on 993"
                      sx={{ ml: 0, my: 0 }}
                    />
                    <Box sx={rowSx}>
                      <TextField sx={halfFieldSx} {...denseFieldProps} label="IMAP Username" name="imapUsername" value={credentials.imapUsername} onChange={handleChange} />
                      <TextField sx={halfFieldSx} {...denseFieldProps} type="password" label="IMAP Password" name="imapPassword" value={credentials.imapPassword} onChange={handleChange} />
                    </Box>
                    <TextField fullWidth {...denseFieldProps} label="Folder - usually INBOX" name="imapFolder" value={credentials.imapFolder} onChange={handleChange} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleTestImap}
                        disabled={testingImap || testingSmtp || loading}
                        startIcon={testingImap ? <CircularProgress size={14} /> : null}
                      >
                        {testingImap ? 'Testing IMAP…' : 'Test IMAP'}
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>SMTP (outbound replies)</Typography>
                      <Button size="small" onClick={copyImapToSmtp}>Copy from IMAP</Button>
                    </Box>
                    <Box sx={rowSx}>
                      <TextField sx={{ flex: 2, minWidth: 260 }} {...denseFieldProps} label="SMTP Host" name="smtpHost" value={credentials.smtpHost} onChange={handleChange} />
                      <TextField sx={{ width: 110 }} {...denseFieldProps} label="Port - 25/587=STARTTLS" name="smtpPort" value={credentials.smtpPort} onChange={handleChange} />
                    </Box>
                    <FormControlLabel
                      control={<Switch name="smtpUseTls" checked={credentials.smtpUseTls} onChange={handleChange} size="small" />}
                      label="SMTP STARTTLS - recommended for ports 25 and 587"
                      sx={{ ml: 0, my: 0 }}
                    />
                    <Box sx={rowSx}>
                      <TextField sx={halfFieldSx} {...denseFieldProps} label="SMTP Username" name="smtpUsername" value={credentials.smtpUsername} onChange={handleChange} />
                      <TextField sx={halfFieldSx} {...denseFieldProps} type="password" label="SMTP Password" name="smtpPassword" value={credentials.smtpPassword} onChange={handleChange} />
                    </Box>
                    <Box sx={rowSx}>
                      <TextField sx={halfFieldSx} {...denseFieldProps} label="From Email - replies sent from this address" name="fromEmail" value={credentials.fromEmail} onChange={handleChange} />
                      <TextField sx={halfFieldSx} {...denseFieldProps} label="From Display Name" name="fromDisplayName" value={credentials.fromDisplayName} onChange={handleChange} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sendTestEmail}
                            onChange={(e) => setSendTestEmail(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Send test email to From address"
                        sx={{ ml: 0, my: 0 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleTestSmtp}
                        disabled={testingImap || testingSmtp || loading}
                        startIcon={testingSmtp ? <CircularProgress size={14} /> : null}
                      >
                        {testingSmtp ? 'Testing SMTP…' : 'Test SMTP'}
                      </Button>
                    </Box>
                    <TextField
                      fullWidth
                      {...denseFieldProps}
                      label="Default CC emails (optional - comma-separated, visible to customer)"
                      name="defaultReplyCcEmails"
                      value={credentials.defaultReplyCcEmails}
                      onChange={handleChange}
                      placeholder="manager@company.com, audit@company.com"
                    />
                    <TextField
                      fullWidth
                      {...denseFieldProps}
                      label="Default BCC emails (optional - comma-separated, hidden from customer)"
                      name="defaultReplyBccEmails"
                      value={credentials.defaultReplyBccEmails}
                      onChange={handleChange}
                      placeholder="owner@company.com"
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedSection === 'appearance'}
                  onChange={() => toggleSection('appearance')}
                  disableGutters
                  elevation={0}
                  sx={accordionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Email appearance</Typography>
                      {expandedSection !== 'appearance' && (credentials.replyHeaderHtml || credentials.replyFooterHtml) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Header and footer configured
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0, pb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
                        Reply header — shown above agent reply
                      </Typography>
                      {renderTemplateEditor({
                        section: 'header',
                        value: credentials.replyHeaderHtml,
                        onChange: handleReplyHeaderChange,
                        placeholder: 'Thank you for contacting support.',
                        containerRef: headerEditorContainerRef,
                      })}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
                        Reply footer — shown below reply, quotes after footer
                      </Typography>
                      {renderTemplateEditor({
                        section: 'footer',
                        value: credentials.replyFooterHtml,
                        onChange: handleReplyFooterChange,
                        placeholder: 'Best regards, Support Team',
                        containerRef: footerEditorContainerRef,
                      })}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Use the Image toolbar button to add logos or banners by URL. Click an existing image to resize or edit it.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Threading uses In-Reply-To / References headers.{' '}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={openAppearance}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Customize header &amp; footer
                </Link>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, py: 1 }}>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>Save & Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog open={imageDialog.open} onClose={clearImageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{imageDialog.mode === 'edit' ? 'Edit image' : 'Insert image'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <TextField
              label="Image URL"
              value={imageDialog.imageUrl}
              onChange={(event) => handleImageDialogFieldChange('imageUrl', event.target.value)}
              placeholder="https://example.com/logo.png"
              fullWidth
              helperText={`Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')} (${MAX_IMAGE_URL_LENGTH} char max)`}
              {...denseFieldProps}
            />
            <TextField
              label="Alt text (optional)"
              value={imageDialog.altText}
              onChange={(event) => handleImageDialogFieldChange('altText', event.target.value)}
              fullWidth
              {...denseFieldProps}
            />
            <TextField
              select
              label="Size preset"
              value={imageDialog.preset}
              onChange={(event) => handleImagePresetChange(event.target.value)}
              fullWidth
              {...denseFieldProps}
            >
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Image width: {clampImageWidth(imageDialog.width)}px</Typography>
              <Slider
                value={clampImageWidth(imageDialog.width)}
                min={MIN_IMAGE_WIDTH}
                max={MAX_IMAGE_WIDTH}
                step={10}
                onChange={handleImageWidthChange}
                valueLabelDisplay="auto"
              />
            </Box>

            <TextField
              select
              label="Alignment"
              value={imageDialog.alignment}
              onChange={(event) => handleImageDialogFieldChange('alignment', event.target.value)}
              fullWidth
              {...denseFieldProps}
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </TextField>

            {imageDialog.validationError && (
              <Typography color="error" variant="body2">{imageDialog.validationError}</Typography>
            )}

            {imageDialog.imageUrl?.trim() && isValidImageUrl(imageDialog.imageUrl.trim()) && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Preview (external image)
                </Typography>
                <Box sx={{ minHeight: 36 }}>
                  <img
                    src={imageDialog.imageUrl.trim()}
                    alt={imageDialog.altText || 'Preview'}
                    width={clampImageWidth(imageDialog.width)}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: imageDialog.alignment === 'center'
                        ? '8px auto'
                        : (imageDialog.alignment === 'right' ? '8px 0 8px auto' : '8px auto 8px 0'),
                    }}
                  />
                </Box>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              Remote images may be blocked by some email clients until recipients choose to display images.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearImageDialog}>Cancel</Button>
          <Button variant="contained" onClick={applyImageChanges}>
            {imageDialog.mode === 'edit' ? 'Apply changes' : 'Insert image'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailCredentialsModal;
